//
// Copyright (c) 2019-Present Syncari - All rights reserved.
//
import { Icon, Tooltip } from 'antd';
import cx from 'classnames';
import { isUndefined } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useI18nContext, withI18n } from 'components/I18nProvider';
import { getDisplayLabel, parseFuzzyValue } from '../FuzzySelect/utils/fuzzyValueHelpers';
import InputContainer from 'components/inputs/InputContainer';
import {
  ConditionValue,
  InputDataType,
  LeftValue,
  OperatorValue,
  PicklistValue,
  PicklistValues,
  RightValue,
  RightOption,
} from 'components/inputs/types';
import { HStack } from 'components/layout';
import { DataSourceFieldPickerValue } from 'pages/insights-studio/dataset/configuration/DataSourceFieldPicker';
import { canShowToken, isTokenSupportedDatatype } from 'pages/sync-studio/node-config/utils';
import AppConstants from 'utils/AppConstants';

import FieldGroup from '../FieldGroup';
import { SingleTokenRegex, TOKEN_BEGIN_SENTINEL, TOKEN_END_SENTINEL } from '../tokens/constants';
import ConditionReadOnly from './ConditionReadOnly';
import ConditionRightValue from './ConditionRightValue';
import HiddenLabel from './HiddenLabel';

import './index.less';

const { READONLY } = AppConstants.INPUT_DISPLAY_MODE;

const DEPENDANT_TYPE_OPERATOR = 'Operator';
const DEPENDANT_TYPE_RIGHT = 'Right';

export const USER_INPUTED_TOKEN_ID = 'USER_INPUTED_TOKEN';

// Pure-fuzzy operator base names; used to detect a saved op whose field has had fuzzy disabled.
const FUZZY_ONLY_OPERATORS = new Set([
  'soundex',
  'similarity',
  'levenshtein_less_equal',
  'difference',
  'daitch_mokotoff',
  'word_similarity',
]);

// List of input types that the condition right will use the autocomple input. Inputs that need raw value
const NEED_PICKLIST_VALUES_INPUT_TYPES = [
  AppConstants.INPUT_TYPE.REFERENCE,
  AppConstants.INPUT_TYPE.PICKLIST,
  AppConstants.INPUT_TYPE.REFERENCE_DATA,
];

const needPicklistValues = (datatype: any): datatype is typeof NEED_PICKLIST_VALUES_INPUT_TYPES => {
  return NEED_PICKLIST_VALUES_INPUT_TYPES.includes(datatype);
};

function getValueForDatatypeAndOperator(
  currentValue: undefined | RightValue,
  datatype?: InputDataType,
  operator?: OperatorValue
): RightValue | undefined {
  if (operator?.unary) {
    return { type: 'literal', value: '' } as const;
  }

  if (currentValue) {
    return currentValue;
  }

  switch (datatype) {
    case 'boolean':
    case 'checkbox':
      return { type: 'literal', value: false } as const;
  }
}

const userLhsTokenDefaults = {
  id: USER_INPUTED_TOKEN_ID,
  datatype: 'string',
  type: 'variable',
};

export interface FetchPicklistParams {
  id?: string;
  dependantType?: string;
  operatorType?: string;
  dependantId?: string;
  params?: Record<string, any>;
}

export interface LHSTokenOption {
  inputCapturer?: React.Dispatch<React.SetStateAction<string>>;
  isTokenInput?: boolean;
}

export interface ConditionProps {
  name: string;
  className?: string;
  leftDatatype?: string;
  leftRenderType?: string;
  onChange?: (value: ConditionValue) => void;
  defaultValue?: ConditionValue;
  values?: LeftValue[];
  fetchPicklistValues?: (param: FetchPicklistParams) => void;
  picklistValues: PicklistValues<PicklistValue[]>;
  displayMode?: string;
  operatorType?: string;
  rightType?: string;
  lhsDisabled?: boolean;
  allowUserToken?: boolean;
  rightValues?: RightOption[];
  allFieldsDisabled?: boolean;
  hideLeftInput?: boolean;
  verticalLayout?: boolean;
  placeHolder?: string;
  context?: string;
  leftOptions?: JSX.Element[];
}

const Condition = ({
  className,
  leftDatatype,
  leftRenderType,
  onChange,
  name,
  defaultValue = {},
  values,
  fetchPicklistValues,
  picklistValues,
  displayMode,
  operatorType = DEPENDANT_TYPE_OPERATOR,
  rightType = DEPENDANT_TYPE_RIGHT,
  lhsDisabled,
  allowUserToken,
  rightValues: rightOptions,
  allFieldsDisabled,
  hideLeftInput,
  verticalLayout,
  placeHolder,
  context,
  leftOptions,
}: ConditionProps) => {
  const { tn } = useI18nContext();
  const [value, setValue] = useState<ConditionValue>(() => ({ ...defaultValue, name }));

  // Operator picklist values
  const [operatorValues, setOperatorValues] = useState<OperatorValue[]>();
  const [rightValues, setRightValues] = useState<PicklistValue[]>();
  const [isUnary, setIsUnary] = useState(false);
  const [rightDisplayValue, setRightDisplayValue] = useState<string | React.ReactNode>('');
  // Operator selected picklist value
  const [operatorValue, setOperatorValue] = useState<OperatorValue>();
  const [fuzzyNotSupported, setFuzzyNotSupported] = useState(false);

  const [doneFirstRender, setDoneFirstRender] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [isTokenInput, setIsTokenInput] = useState(defaultValue.left?.id === USER_INPUTED_TOKEN_ID);
  const [leftValues, setLeftValues] = useState(() => {
    if (!allowUserToken || isUndefined(values) || defaultValue.left?.id !== USER_INPUTED_TOKEN_ID) {
      return values;
    }

    return [
      ...values,
      {
        ...userLhsTokenDefaults,
        label: defaultValue.left?.value,
        value: defaultValue.left?.value,
      } as LeftValue,
    ];
  });

  useEffect(() => {
    if (allowUserToken) {
      if (doneFirstRender) {
        setLeftValues((previousValues) => {
          const filteredLeftValues = previousValues?.filter((value) => value.id !== USER_INPUTED_TOKEN_ID);

          return filteredLeftValues
            ? [
                ...filteredLeftValues,
                {
                  ...userLhsTokenDefaults,
                  label: searchValue,
                  value: searchValue,
                } as LeftValue,
              ]
            : previousValues;
        });
      } else {
        setDoneFirstRender(true);
      }
    } else {
      setLeftValues(values);
    }
  }, [doneFirstRender, searchValue, allowUserToken, values]);

  useEffect(() => {
    if (allowUserToken && doneFirstRender) {
      setIsTokenInput(!!searchValue.match(SingleTokenRegex));
    }
  }, [allowUserToken, doneFirstRender, searchValue]);

  useEffect(() => {
    if (value?.left?.value) {
      _fetchPicklistValues({
        dependantType: operatorType,
        dependantId: value.left.value,
        params: {
          datatype: value.left?.datatype,
        },
      });

      if (needPicklistValues(value?.left?.datatype)) {
        _fetchPicklistValues({
          dependantType: rightType,
          dependantId: value.left.value,
          params: {
            datatype: value.left?.datatype,
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, operatorValues]);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const onLhsSearchChange = (input: string) => {
    setSearchValue(input);
  };

  const onRightChange = useCallback(
    (right: RightValue) => {
      const newVal = {
        ...value,
        right,
      };

      setValue(newVal);
      onChange?.(newVal);
    },
    [onChange, value]
  );

  let showTokens = canShowToken(value?.left);

  const rightValueComponent = useMemo(() => {
    // Skip the right value only if it's unary
    // Always render the component (but disabled) when we have a left value
    // This ensures the right input stays visible during left field changes

    if (isUnary) {
      return;
    }

    if (operatorValue?.datatype) {
      showTokens = isTokenSupportedDatatype(operatorValue.datatype);
    }

    // Use the base operator (without ":percent"/":typos" suffix) so dragging the
    // fuzzy slider doesn't remount the right-value input and clobber its local state.
    const operatorKey = typeof value?.operator === 'string' ? parseFuzzyValue(value.operator).value : value?.operator;

    return (
      <ConditionRightValue
        key={`condition-right-input-container-${name}-${value?.left?.value}-${operatorKey}`}
        defaultValue={value?.right}
        showTokens={showTokens}
        disabled={!Boolean(value?.left?.value && operatorValue) || allFieldsDisabled}
        leftValue={value?.left}
        name={name}
        displayMode={displayMode}
        onChange={onRightChange}
        operatorValue={operatorValue}
        value={value?.right}
        values={rightValues}
        rightOptions={rightOptions}
        placeHolder="Enter value"
      />
    );
  }, [
    allFieldsDisabled,
    displayMode,
    isUnary,
    name,
    onRightChange,
    operatorValue,
    rightValues,
    showTokens,
    value?.left,
    value?.operator,
    value?.right,
    placeHolder,
  ]);

  useEffect(() => {
    if (!isUndefined(value?.left?.value)) {
      let operatorVals = picklistValues[`${encodeURIComponent(value?.left?.value ?? '')}/${name}${operatorType}`];
      operatorVals && setOperatorValues(operatorVals as OperatorValue[]);
      if (needPicklistValues(value?.left?.datatype)) {
        let rightVals = picklistValues[`${encodeURIComponent(value?.left?.value ?? '')}/${name}${rightType}`];
        if (rightVals?.length) {
          setRightValues(rightVals);

          if (value?.right?.value) {
            const rightPick = rightVals.find((v: PicklistValue) => v.value === value.right?.value);
            if (rightPick?.label) {
              setRightDisplayValue(rightPick.label);
            } else if (value.right?.value) {
              setRightDisplayValue(String(value.right.value));
            }
            return;
          }
        }
      }
    }
    if (!isUndefined(value?.right?.value)) {
      setRightDisplayValue(
        typeof value?.right?.value === 'object' && !Array.isArray(value?.right?.value)
          ? rightValueComponent
          : String(value?.right?.value)
      );
    }
  }, [picklistValues, value, name, operatorType, rightType, rightValueComponent]);

  const _fetchPicklistValues = useCallback(
    (options: FetchPicklistParams) => {
      options.dependantId = encodeURIComponent(options.dependantId ?? '');
      const id = `${options.dependantId}/${name}${options.dependantType}`;

      if (picklistValues?.[id]) {
        return picklistValues[id];
      } else {
        fetchPicklistValues && fetchPicklistValues({ ...options, id, ...(context && { context }) });
      }
    },
    [fetchPicklistValues, name, picklistValues, context]
  );

  const onLeftChange = useCallback(
    (val: string | DataSourceFieldPickerValue) => {
      const left =
        typeof val === 'string'
          ? leftValues?.find((v: LeftValue) => v.value === val)
          : leftValues?.find((leftValue: LeftValue) =>
              val.datasourceAlias
                ? leftValue.id === val.id && leftValue.datasourceAlias === val.datasourceAlias
                : leftValue.id === val.id
            );

      const newVal = {
        ...value,
        left,
        operator: undefined,
        right: undefined,
      };

      // TODO: are we duplicating state tracking? shouldn't
      // the onChange affect the incoming value instead of
      // tracking in 2 places?
      setValue(newVal);
      onChange?.({ ...newVal, name });
      _fetchPicklistValues({
        dependantId: left?.value,
        dependantType: operatorType,
        params: {
          datatype: newVal.left?.datatype,
        },
      });
      if (needPicklistValues(newVal.left?.datatype)) {
        _fetchPicklistValues({
          dependantId: left?.value,
          dependantType: rightType,
          params: {
            datatype: newVal.left?.datatype,
          },
        });
      }
    },
    [_fetchPicklistValues, leftValues, name, onChange, operatorType, rightType, value]
  );

  const onOperatorChange = (operator: string) => {
    // Smart operator detection: try normal operator first, then fuzzy if needed
    let operatorObject: PicklistValue | undefined;

    // Step 1: Try to find exact match (normal operator)
    operatorObject = operatorValues?.find((op: PicklistValue) => op.value === operator);

    // Step 2: If not found, check if it's a fuzzy operator
    if (!operatorObject) {
      // Parse fuzzy value to get base operator (e.g., "resembles:70" -> "resembles")
      const { value: baseOperatorValue } = parseFuzzyValue(operator);

      // Find the base operator
      const baseOperator = operatorValues?.find((op: PicklistValue) => op.value === baseOperatorValue);

      // Only use fuzzy parsing if the base operator supports fuzzy options
      if (baseOperator && (baseOperator.hasFuzzyOption || baseOperator.hasMinMaxTypoConfig)) {
        operatorObject = baseOperator;
      }
    }

    // Detect a fuzzy slider tweak: same base operator, only the ":percent"/":typos" suffix differs.
    // Without this guard, dragging the slider would call getValueForDatatypeAndOperator with
    // currentValue=undefined and wipe the RHS text box on every step.
    const prevOperatorStr = typeof value?.operator === 'string' ? value.operator : '';
    const { value: prevBaseOperator } = parseFuzzyValue(prevOperatorStr);
    const { value: newBaseOperator } = parseFuzzyValue(operator);
    const isFuzzySliderUpdate = Boolean(
      operatorObject &&
        (operatorObject.hasFuzzyOption || operatorObject.hasMinMaxTypoConfig) &&
        prevBaseOperator &&
        prevBaseOperator === newBaseOperator
    );

    const newVal = {
      ...value,
      right: isFuzzySliderUpdate
        ? value?.right
        : getValueForDatatypeAndOperator(undefined, value?.left?.datatype, operatorObject as OperatorValue),
      operator,
    };

    setValue(newVal);
    onChange?.(newVal);
  };

  const dropdownRender = useMemo(() => {
    return allowUserToken
      ? (menu: React.ReactNode, props: any) => (
          <div>
            {menu}
            <div
              onMouseDown={() => {
                onLeftChange(searchValue);
              }}
              className={cx('user-token-condition-option', !isTokenInput && 'user-token-condition-option--disabled')}>
              {'Use entered Token Value'}
              <Tooltip
                title={
                  isTokenInput
                    ? `${searchValue}`
                    : // NOTE: there seems to be a bug with i18n when you substute in {{ and }} as string values. So we have to
                      // "pass" the token sentinels in this way instead of the built in way.
                      `${tn('token_value_help_1')}'${TOKEN_BEGIN_SENTINEL}'${tn(
                        'token_value_help_2'
                      )}'${TOKEN_END_SENTINEL}'.`
                }>
                <Icon type="info-circle" theme="filled" className="user-token-condition-option__icon" />
              </Tooltip>
            </div>
          </div>
        )
      : undefined;
  }, [allowUserToken, isTokenInput, onLeftChange, searchValue, tn]);

  // Update operator value when operator changes
  useEffect(() => {
    if (value?.operator && operatorValues) {
      // First, try to find exact match (normal operator)
      const operatorObject = operatorValues.find((op: PicklistValue) => op.value === value.operator);

      if (operatorObject) {
        // Found exact match - use it
        operatorObject && setIsUnary(operatorObject.unary);
        setOperatorValue(operatorObject);
        setFuzzyNotSupported(false);
      } else {
        // Not found - parse as fuzzy value (e.g., "resembles:70" -> "resembles")
        const parsed = parseFuzzyValue(value.operator);
        const opVal = operatorValues.find((op: PicklistValue) => op.value === parsed.value);

        if (opVal && (opVal.hasFuzzyOption || opVal.hasMinMaxTypoConfig)) {
          // Found fuzzy operator - set it as the operator value
          opVal && setIsUnary(opVal.unary);
          setOperatorValue(opVal);
          setFuzzyNotSupported(false);
        } else {
          // Fuzzy-looking saved op with no matching fuzzy support: preserve value and warn instead of wiping.
          const isFuzzyForm = parsed.percentage !== null;
          const isFuzzyBaseName = FUZZY_ONLY_OPERATORS.has(parsed.value);
          setOperatorValue(undefined);
          setIsUnary(false);

          if (isFuzzyForm || isFuzzyBaseName) {
            setFuzzyNotSupported(true);
          } else {
            setFuzzyNotSupported(false);
            const newVal = {
              ...value,
              operator: operatorObject,
            };
            onChange?.(newVal);
          }
        }
      }
    } else {
      setFuzzyNotSupported(false);
    }
  }, [value?.operator, operatorValues]);

  return (
    <>
      {displayMode === READONLY ? (
        <ConditionReadOnly
          className={className}
          left={leftValues?.find((lval) => value?.left?.value === lval.value)?.label || value?.left?.label}
          operator={value?.operator}
          operatorLabel={operatorValue?.label}
          right={rightDisplayValue}
        />
      ) : (
        <HStack
          align="start"
          className={cx('synri-condition', className, {
            'data-studio-vertical-layout': verticalLayout,
            'data-studio-hide-left': hideLeftInput,
          })}>
          <div className="synri-condition-left" key={`condition-left-input-container-${name}`}>
            <FieldGroup label={showTokens && <HiddenLabel>{tn('condition_lhs')}</HiddenLabel>}>
              <InputContainer
                dropdownMatchSelectWidth={false}
                key={`condition-left-input-${name}`}
                aria-label={tn('condition_lhs')}
                disabled={lhsDisabled || allFieldsDisabled}
                useDataTypeBadges
                datatype={leftDatatype || AppConstants.INPUT_TYPE.PICKLIST}
                renderType={leftRenderType}
                values={leftValues}
                defaultValue={!value?.left?.datasourceAlias ? value?.left?.value : value?.left}
                onChange={onLeftChange}
                onSearchChange={allowUserToken ? onLhsSearchChange : undefined}
                isTokenInput={isTokenInput}
                dropdownRender={dropdownRender}
                placeholder={`${placeHolder ?? 'Select field'}`}
                {...(leftOptions
                  ? {
                      options: leftOptions,
                      filterOption: (input: string, option: any) =>
                        (option.props.title || '').toLowerCase().includes(input.toLowerCase()),
                    }
                  : {})}
              />
            </FieldGroup>
          </div>
          <div className="synri-condition-operator" key={`condition-operator-input-container-${name}`}>
            <FieldGroup label={showTokens && <HiddenLabel>{tn('condition_operator')}</HiddenLabel>}>
              <InputContainer
                key={`condition-operator-input-${name}-${value?.left?.value}`}
                aria-label={tn('condition_operator')}
                datatype={AppConstants.INPUT_TYPE.PICKLIST}
                defaultValue={value?.operator}
                disabled={!value?.left?.value || allFieldsDisabled}
                values={operatorValues}
                onChange={onOperatorChange}
                placeholder="Select"
              />
            </FieldGroup>
            {fuzzyNotSupported && (
              <Tooltip title={tn('fuzzy_not_supported_warning')}>
                <Icon
                  type="exclamation-circle"
                  theme="filled"
                  className="synri-condition-fuzzy-warning"
                  aria-label={tn('fuzzy_not_supported_warning')}
                />
              </Tooltip>
            )}
          </div>
          {rightValueComponent}
        </HStack>
      )}
    </>
  );
};

export default withI18n(Condition, 'Filter');
